import { toast } from 'react-toastify';
import { all, call, fork, put, take, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { FailToastContainer, SuccessToastContainer } from 'components/Toast/ToastContainer';
import { appConfig } from 'config/app';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { getEmployersRequest } from 'store/Employers';
import {
  ChannelType,
  IAction,
  IAvatarUrlResponse,
  ICardDetails,
  IDeleteUserPayload,
  IIdentity,
  IInvite,
  IUpdateUserPasswordPayloadProps,
  IUpdateUserPayload,
  IUser,
  IUserInvites,
  IVerifyIdentity,
  PaymentNotificationMethod,
  UserForm,
} from 'types';
import { resizeImage, stripCountryCode } from 'utils';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import {
  acceptAllUserInvitesFail,
  acceptAllUserInvitesSuccess,
  deleteUserFail,
  deleteUserSuccess,
  getCurrentUserCardFail,
  getCurrentUserCardRequest,
  getCurrentUserCardSuccess,
  getCurrentUserFail,
  getCurrentUserRequest,
  getCurrentUserSuccess,
  getUserInvitesFail,
  getUserInvitesSuccess,
  resendIdentityVerificationCodeFail,
  resendIdentityVerificationCodeSuccess,
  updateCurrentUserRequest,
  updateCurrentUserSuccess,
  updateCurrentUserUserFail,
  updateDateOfBirthFail,
  updateDateOfBirthSuccess,
  updateIdentityVerificationCodeFail,
  updateIdentityVerificationCodeSuccess,
  updatePaymentNotificationMethodFail,
  updatePaymentNotificationMethodSuccess,
  updateSMSFail,
  updateSMSSuccess,
  updateUserEmailFail,
  updateUserEmailSuccess,
  updateUsernameFail,
  updateUsernameSuccess,
  updateUserPasswordFail,
  updateUserPasswordSuccess,
  uploadAvatarFail,
} from './actions';
import * as ActionTypes from './types';

export function transformToFormData(payload: IAvatarUrlResponse) {
  const formData = new FormData();
  Object.entries(payload.fields).forEach(([key, value]) => formData.append(key, value));

  return formData;
}

export function getNewAvatarUrl(avatarKey: string) {
  return `${appConfig.AVATAR.BASE_URL}${avatarKey}?${Date.now()}`;
}

export function* getAvatarUrl({ payload }: IAction<ActionTypes.User.GET_AVATAR_URL_REQUEST, { onSuccess?: (avatarUrl?: IAvatarUrlResponse) => void }>) {
  try {
    const { onSuccess } = payload;
    const { body } = yield call(API.Users.createAvatarUrl);

    body.fields.acl = 'public-read';

    if (onSuccess) yield call(onSuccess, body);
  } catch (error: any) {
    yield call(toast, FailToastContainer({ message: 'There was an error uploading your avatar' }));
  }
}

export function* uploadAvatar({ payload }: IAction<ActionTypes.User.UPLOAD_AVATAR_REQUEST, { data: IAvatarUrlResponse; onSuccess?: () => void }>) {
  const { data, onSuccess } = payload;

  try {
    const resizedImage: File = yield call(resizeImage, { file: data.fields.file, maxWidth: appConfig.AVATAR.RESIZE_WIDTH, maxHeight: appConfig.AVATAR.RESIZE_HEIGHT, outputType: 'file', compressFormat: 'JPEG' });

    data.fields.file = resizedImage;

    const transformedData: FormData = yield call(transformToFormData, data);

    yield call(fetch, data?.url, {
      method: 'POST',
      body: transformedData,
    });

    yield put(updateCurrentUserRequest({ avatarUrl: yield call(getNewAvatarUrl, data.fields.key) }, UserForm.SETTINGS, undefined, 'Successfully uploaded avatar'));

    if (onSuccess) yield call(onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });

    yield put(uploadAvatarFail(formattedError));
  }
}

export function* updateCurrentUser({
  payload,
}: IAction<
  ActionTypes.User.UPDATE_CURRENT_USER_REQUEST,
  {
    data: IUpdateUserPayload | IUpdateUserPasswordPayloadProps | IUser;
    form: UserForm;
    onSuccess?: () => void;
    toastMessage?: string;
  }
>) {
  const { data, onSuccess, toastMessage, form } = payload;
  try {
    const response: ApiResponse<IUser> = yield call(API.Users.update, data);

    yield put(updateCurrentUserSuccess(response.body));

    if (onSuccess) yield call(onSuccess);

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    yield put(updateCurrentUserUserFail(formattedError, form));
  }
}

export function* updateDateOfBirth({ payload }: IAction<ActionTypes.User.UPDATE_DATE_OF_BIRTH_REQUEST, { data: IUpdateUserPayload; toastMessage?: string }>) {
  const { data, toastMessage } = payload;
  try {
    const response: ApiResponse<IUser> = yield call(API.Users.update, data);

    yield put(updateDateOfBirthSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    yield put(updateDateOfBirthFail(formattedError, error.statusCode >= 500));
  }
}

export function* updateUsername({ payload }: IAction<ActionTypes.User.UPDATE_USER_NAME_REQUEST, { data: IUpdateUserPayload; toastMessage?: string }>) {
  const { data, toastMessage } = payload;
  try {
    const response: ApiResponse<IUser> = yield call(API.Users.update, data);

    yield put(updateUsernameSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    yield put(updateUsernameFail(formattedError));
  }
}

export function* updateUserPassword({ payload }: IAction<ActionTypes.User.UPDATE_USER_PASSWORD_REQUEST, { data: IUpdateUserPayload; toastMessage?: string }>) {
  const { data, toastMessage } = payload;
  try {
    const response: ApiResponse<IUser> = yield call(API.Users.update, data);

    yield put(updateUserPasswordSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    yield put(updateUserPasswordFail(formattedError, error.statusCode >= 500));
  }
}

export function* getCurrentUserCardAndDetail(): any {
  yield put(getCurrentUserRequest());
  const getUserSuccess = yield take(ActionTypes.User.GET_CURRENT_USER_SUCCESS);
  const userPayload = getUserSuccess?.payload;

  if (userPayload?.card?.id) {
    yield put(getCurrentUserCardRequest(userPayload?.card?.id));
  }
}

export function* getCurrentUser({ payload }: IAction<ActionTypes.User.GET_CURRENT_USER_REQUEST, { onSuccess?: (user: IUser) => void; onFail?: (error: any) => void }>) {
  const { onSuccess, onFail } = payload;

  try {
    const response: ApiResponse<IUser> = yield call(API.Users.show);
    if (onSuccess) yield call(onSuccess, response.body);

    yield put(getCurrentUserSuccess(response.body));
  } catch (error) {
    if (onFail) yield call(onFail, error);
    yield put(getCurrentUserFail(error));
  }
}

export function* updatePaymentNotificationMethod({ payload }: IAction<ActionTypes.User.UPDATE_PAYMENT_NOTIFICATION_METHOD_REQUEST, { paymentNotificationMethod: PaymentNotificationMethod }>) {
  try {
    const { paymentNotificationMethod } = payload;
    const response: ApiResponse<IUser> = yield call(API.Users.update, { settings: { paymentNotificationMethod } });

    yield call(toast, SuccessToastContainer({ message: 'Notifications updated successfully.' }));

    yield put(updatePaymentNotificationMethodSuccess(response.body));
  } catch (error: any) {
    yield put(updatePaymentNotificationMethodFail(error, error.statusCode >= 500));
  }
}

export function* getCurrentUserCard({ payload }: IAction<ActionTypes.User.GET_CURRENT_USER_CARD_REQUEST, { cardId: string }>) {
  try {
    const { cardId } = payload;
    const response: ApiResponse<ICardDetails> = yield call(API.Cards.getCard, cardId);

    yield put(getCurrentUserCardSuccess(response.body));
  } catch (error) {
    yield put(getCurrentUserCardFail(error));
  }
}

export function* deleteUser({ payload }: IAction<ActionTypes.User.DELETE_USER_REQUEST, { data?: IDeleteUserPayload; toastMessage?: string }>) {
  try {
    const { data, toastMessage } = payload;
    const response: ApiResponse<IUser> = yield call(API.Users.delete, { dateOfBirth: data?.dateOfBirth });

    yield put(deleteUserSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error: any) {
    yield put(deleteUserFail(error));
  }
}

export function* updateSMS({ payload }: IAction<ActionTypes.User.UPDATE_SMS_REQUEST, { identityValue: string; toastMessage?: string }>) {
  try {
    const { identityValue, toastMessage } = payload;

    const response: ApiResponse<IIdentity> = yield call(API.Identity.createIdentity, { identity: stripCountryCode(identityValue) });

    yield put(updateSMSSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(updateSMSFail(formattedError));
  }
}

export function* updateUserEmail({ payload }: IAction<ActionTypes.User.UPDATE_USER_EMAIL_REQUEST, { identityValue: string; toastMessage?: string }>) {
  try {
    const { identityValue, toastMessage } = payload;

    const response: ApiResponse<IIdentity> = yield call(API.Identity.createIdentity, { identity: identityValue });

    yield put(updateUserEmailSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error: any) {
    yield put(updateUserEmailFail(error));
  }
}

export function* resendIdentityVerificationCode({ payload }: IAction<ActionTypes.User.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST, { identityId: string; toastMessage?: string; channel?: ChannelType.CALL }>) {
  try {
    const { identityId, toastMessage, channel } = payload;

    const response: ApiResponse<IVerifyIdentity> = yield call(API.Identity.resendVerificationCode, identityId, channel);

    yield put(resendIdentityVerificationCodeSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error) {
    yield put(resendIdentityVerificationCodeFail(error));
  }
}

export function* updateIdentityVerificationCode({ payload }: IAction<ActionTypes.User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST, { identityId: string; verificationCode: string; toastMessage?: string }>) {
  try {
    const { identityId, verificationCode, toastMessage } = payload;

    const response: ApiResponse<IVerifyIdentity> = yield call(API.Identity.verifyIdentity, identityId, { verifyCode: verificationCode });

    yield put(updateIdentityVerificationCodeSuccess(response.body));

    if (toastMessage) yield call(toast, SuccessToastContainer({ message: toastMessage }));
  } catch (error) {
    yield put(updateIdentityVerificationCodeFail(error));
  }
}

interface IAcceptInvitesPayload {
  userInvites: IInvite[];
  onSuccess?: (invitesResponse?: IInvite[]) => void;
}

export function* getUserInvites({ payload }: IAction<ActionTypes.User, Omit<IAcceptInvitesPayload, 'userInvites'>>) {
  try {
    const response: ApiResponse<IUserInvites> = yield call(API.Users.listInvites);
    yield put(getUserInvitesSuccess(response.body));

    // Execute on success function
    if (payload?.onSuccess) yield call(payload.onSuccess, response.body.invites);
  } catch (error: any) {
    yield put(getUserInvitesFail(error));
  }
}

export function* acceptUserInvite(inviteId: string) {
  const response: ApiResponse<IInvite> = yield call(API.Invites.accept, inviteId);
  return response.body;
}

export function* acceptAllUserInvites({ payload }: IAction<ActionTypes.User, IAcceptInvitesPayload>) {
  let { userInvites } = payload;

  try {
    userInvites = yield all(payload.userInvites.map((invite) => call(acceptUserInvite, invite.id)));

    yield put(acceptAllUserInvitesSuccess(userInvites));

    // Reload employers after successfully accepting invites
    yield put(getEmployersRequest());

    yield call(toast, SuccessToastContainer({ message: `Successfully accepted ${userInvites.length > 1 ? 'invitations' : 'invitation'}`, dataCy: 'invite-accepted-toast' }));

    // Execute on success function
    if (payload.onSuccess) yield call(payload.onSuccess, userInvites);
  } catch (error: any) {
    yield put(acceptAllUserInvitesFail(error));
    yield call(browserHistory.replace, routeConstants.private.rootPath);
  }
}

export function* watchGetCurrentUserCard() {
  yield takeEvery(ActionTypes.User.GET_CURRENT_USER_CARD_REQUEST, getCurrentUserCard);
}

export function* watchGetAvatarUrl() {
  yield takeEvery(ActionTypes.User.GET_AVATAR_URL_REQUEST, getAvatarUrl);
}

export function* watchUploadAvatar() {
  yield takeEvery(ActionTypes.User.UPLOAD_AVATAR_REQUEST, uploadAvatar);
}

export function* watchUpdateCurrentUser() {
  yield takeEvery(ActionTypes.User.UPDATE_CURRENT_USER_REQUEST, updateCurrentUser);
}

export function* watchGetCurrentUserCardAndDetail() {
  yield takeEvery(ActionTypes.User.GET_CURRENT_USER_AND_CARD_DETAIL_REQUEST, getCurrentUserCardAndDetail);
}

export function* watchGetCurrentUser() {
  yield takeEvery(ActionTypes.User.GET_CURRENT_USER_REQUEST, getCurrentUser);
}

export function* watchUpdatePaymentNotificationMethod() {
  yield takeEvery(ActionTypes.User.UPDATE_PAYMENT_NOTIFICATION_METHOD_REQUEST, updatePaymentNotificationMethod);
}

export function* watchUpdateDateOfBirth() {
  yield takeEvery(ActionTypes.User.UPDATE_DATE_OF_BIRTH_REQUEST, updateDateOfBirth);
}

export function* watchUpdateUsername() {
  yield takeEvery(ActionTypes.User.UPDATE_USER_NAME_REQUEST, updateUsername);
}

export function* watchUpdateUserPassword() {
  yield takeEvery(ActionTypes.User.UPDATE_USER_PASSWORD_REQUEST, updateUserPassword);
}

export function* watchDeleteUser() {
  yield takeEvery(ActionTypes.User.DELETE_USER_REQUEST, deleteUser);
}

export function* watchUpdateSMS() {
  yield takeEvery(ActionTypes.User.UPDATE_SMS_REQUEST, updateSMS);
}

export function* watchUpdateUserEmail() {
  yield takeEvery(ActionTypes.User.UPDATE_USER_EMAIL_REQUEST, updateUserEmail);
}

export function* watchResendIdentityVerificationCode() {
  yield takeEvery(ActionTypes.User.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST, resendIdentityVerificationCode);
}

export function* watchUpdateIdentityVerificationCode() {
  yield takeEvery(ActionTypes.User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST, updateIdentityVerificationCode);
}

export function* watchGetUserInvites() {
  yield takeEvery(ActionTypes.User.GET_USER_INVITES_REQUEST, getUserInvites);
}

export function* watchAcceptAllUserInvites() {
  yield takeEvery(ActionTypes.User.ACCEPT_ALL_USER_INVITES_REQUEST, acceptAllUserInvites);
}

function* userSaga() {
  yield all([
    fork(watchGetAvatarUrl),
    fork(watchUpdateCurrentUser),
    fork(watchGetCurrentUserCardAndDetail),
    fork(watchGetCurrentUser),
    fork(watchUpdatePaymentNotificationMethod),
    fork(watchUploadAvatar),
    fork(watchGetCurrentUserCard),
    fork(watchUpdateDateOfBirth),
    fork(watchUpdateUsername),
    fork(watchUpdateUserPassword),
    fork(watchDeleteUser),
    fork(watchUpdateSMS),
    fork(watchResendIdentityVerificationCode),
    fork(watchUpdateIdentityVerificationCode),
    fork(watchUpdateUserEmail),
    fork(watchGetUserInvites),
    fork(watchAcceptAllUserInvites),
  ]);
}

export { userSaga };
